
import React, { useState, useEffect, useRef } from 'react';
import { Word, UserProgress } from '../types';
import { judgeSpokenMeaning } from '../services/geminiService';

interface Props {
  words: Word[];
  onComplete: (results: any[]) => void;
  onUpdateProgress: (wordId: string, status: 'correct' | 'wrong' | 'forgot') => void;
}

const ReviewSession: React.FC<Props> = ({ words, onComplete, onUpdateProgress }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(5);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedback, setFeedback] = useState<{ status: string; text: string } | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const currentWord = words[currentIndex];

  useEffect(() => {
    startNewWord();
    return () => stopTimer();
  }, [currentIndex]);

  const startNewWord = () => {
    setTimeLeft(5);
    setShowAnswer(false);
    setFeedback(null);
    startTimer();
    startRecording();
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = window.setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => audioChunks.current.push(e.data);
      mediaRecorder.current.onstop = processAudio;
      mediaRecorder.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Mic error", err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  };

  const handleTimeout = () => {
    stopTimer();
    stopRecording();
    // Logic for timeout is same as wrong
  };

  const processAudio = async () => {
    setIsProcessing(true);
    const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
    const reader = new FileReader();
    reader.readAsDataURL(audioBlob);
    reader.onloadend = async () => {
      const base64Audio = (reader.result as string).split(',')[1];
      const result = await judgeSpokenMeaning(currentWord.english, currentWord.pos, base64Audio);
      
      if (result.isCorrect) {
        setFeedback({ status: 'correct', text: `正确! 你说了: "${result.transcription}"` });
        onUpdateProgress(currentWord.id, 'correct');
        setTimeout(nextWord, 1500);
      } else {
        setFeedback({ status: 'wrong', text: `错误. 听到了: "${result.transcription}"` });
        setShowAnswer(true);
        onUpdateProgress(currentWord.id, 'wrong');
        setTimeout(nextWord, 3000);
      }
      setIsProcessing(false);
    };
  };

  const handleForgot = () => {
    stopTimer();
    stopRecording();
    setShowAnswer(true);
    setFeedback({ status: 'forgot', text: '已标记为下次复习' });
    onUpdateProgress(currentWord.id, 'forgot');
    setTimeout(nextWord, 2000);
  };

  const nextWord = () => {
    if (currentIndex < words.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onComplete([]); // Simplified
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-10 p-8 bg-white rounded-3xl shadow-xl border border-slate-100">
      <div className="flex justify-between items-center mb-8">
        <span className="text-slate-400 font-medium">进度: {currentIndex + 1} / {words.length}</span>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white shadow-lg ${timeLeft > 2 ? 'bg-blue-500' : 'bg-red-500 animate-pulse'}`}>
          {timeLeft}s
        </div>
      </div>

      <div className="text-center py-12">
        <h2 className="text-6xl font-bold text-slate-800 mb-2">{currentWord.english}</h2>
        <p className="text-xl text-blue-600 font-semibold">{currentWord.pos}</p>
      </div>

      <div className="min-h-[100px] flex flex-col items-center justify-center gap-4">
        {isProcessing && <div className="text-slate-500 italic flex items-center gap-2"><i className="fas fa-spinner animate-spin"></i> AI 正在思考...</div>}
        
        {feedback && (
          <div className={`px-6 py-3 rounded-2xl text-center font-medium ${
            feedback.status === 'correct' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {feedback.text}
          </div>
        )}

        {showAnswer && (
          <div className="text-2xl font-bold text-slate-700 bg-yellow-50 px-8 py-4 rounded-xl border border-yellow-200">
            正确答案：{currentWord.chinese}
          </div>
        )}
      </div>

      <div className="mt-12 flex gap-4">
        <button 
          onClick={handleForgot}
          className="flex-1 py-4 px-6 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-2xl transition-all active:scale-95"
        >
          忘记了 (下轮再练)
        </button>
        <div className="flex-[0.5] flex items-center justify-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white shadow-inner ${isRecording ? 'bg-red-500 animate-bounce' : 'bg-slate-300'}`}>
            <i className={`fas ${isRecording ? 'fa-microphone' : 'fa-microphone-slash'}`}></i>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSession;
